// Frontend/theletterdoctor-admin-app/src/pages/AdminDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Input = styled.input`
  flex: 1;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Select = styled.select`
  flex: 1;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Button = styled.button`
  padding: 10px 20px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover { background-color: #004a94; }
  &.danger { background-color: #d9534f; &:hover { background-color: #c9302c; } }
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td { padding: 12px; border-bottom: 1px solid #ddd; text-align: left; }
  th { background-color: #f2f2f2; }
  tr:hover { background-color: #f9f9f9; cursor: pointer; }
`;

const DetailView = styled.div`
  background: #fff;
  border: 1px solid #ddd;
  padding: 2rem;
  margin-top: 2rem;
  border-radius: 8px;
  position: relative;
`;

const CloseDetailButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  &:hover { color: #333; }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  label { display: block; margin-bottom: 0.5rem; font-weight: bold; }
  input { width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px; }
  select { width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px; }
`;

const OrderCard = styled.div`
  border: 1px solid #eee;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  background-color: #fafafa;
`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const adminName = localStorage.getItem('adminName');
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('users'); // 'users', 'doctors', 'admins'

  const [searchResults, setSearchResults] = useState({ users: [], doctors: [], admins: [] });
  const [selectedItem, setSelectedItem] = useState(null); // { type: 'users', data: {...} }
  const [loading, setLoading] = useState(false);

  // Derived state for easier access
  const selectedUser = selectedItem?.type === 'users' ? selectedItem.data : null;
  const selectedDoctor = selectedItem?.type === 'doctors' ? selectedItem.data : null;
  const selectedAdmin = selectedItem?.type === 'admins' ? selectedItem.data : null;

  const token = localStorage.getItem('adminToken');
  const api = useMemo(() => axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // Redirect if no token is present
  useEffect(() => {
    if (!token) {
      navigate('/');
    }
  }, [token, navigate]);

  // Add interceptor to handle expired sessions (401/403 errors)
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      response => response,
      error => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminName');
          toast.error('Session expired. Please login again.');
          navigate('/');
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptor);
  }, [api, navigate]);

  // Loaded once so the "Change Service" dropdown on each order can offer
  // every service, grouped by category, without a fetch per order card.
  const [services, setServices] = useState([]);
  useEffect(() => {
    if (!token) return;
    api.get('/admins/services')
      .then(res => setServices(res.data.services || []))
      .catch(() => { /* Non-critical - the change-service control just won't have options. */ });
  }, [api, token]);

  const servicesByCategory = useMemo(() => {
    const grouped = {};
    services.forEach(s => {
      const cat = s.category || 'Uncategorised';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(s);
    });
    Object.values(grouped).forEach(list => list.sort((a, b) => a.name.localeCompare(b.name)));
    return grouped;
  }, [services]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminName');
    navigate('/');
  };

  const resetSearchState = () => {
    setSearchResults({ users: [], doctors: [], admins: [] });
    setSelectedItem(null);
  };

  const handleSearch = async (type = searchType) => {
    setLoading(true);
    resetSearchState(); // Reset state before new search
    try {
      const res = await api.get(`/admins/${type}/search?q=${query}`);
      if (type === 'users') {
        setSearchResults(prev => ({ ...prev, users: res.data.users }));
      } else if (type === 'doctors') {
        setSearchResults(prev => ({ ...prev, doctors: res.data.doctors }));
      } else if (type === 'admins') {
        setSearchResults(prev => ({ ...prev, admins: res.data.admins }));
      }
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = () => {
    handleSearch(searchType);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.put(`/admins/users/${selectedUser.id}`, {
        userEmail: selectedUser.originalEmail || selectedUser.email,
        name: selectedUser.name,
        email: selectedUser.email,
        dob: selectedUser.dob,
        phone: selectedUser.phone,
        sex: selectedUser.sex,
        shipping_address: selectedUser.shipping_address
      });
      toast.success('User updated successfully');
      const updatedUserData = res.data.user;
      setSearchResults(prev => ({ ...prev, users: prev.users.map(u => u.id === updatedUserData.id ? updatedUserData : u) }));
      setSelectedItem({ type: 'users', data: updatedUserData });
    } catch (err) {
      toast.error('Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressChange = (field, value) => {
    setSelectedItem(prev => ({
      ...prev,
      data: {
        ...prev.data,
        shipping_address: {
          ...(prev.data.shipping_address || {}),
          [field]: value
        }
      }
    }));
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      const payload = {
        userEmail: selectedUser.email,
        status: 'cancelled'
      };
      await api.put(`/admins/users/${selectedUser.id}/orders/${orderId}`, payload);
      toast.success('Order cancelled');
      // Update local state
      const updatedOrders = selectedUser.orders.map(o =>
        o.orderId === orderId ? { ...o, status: 'cancelled' } : o
      );
      setSelectedItem(prev => ({ ...prev, data: { ...prev.data, orders: updatedOrders } }));
    } catch (err) {
      toast.error('Failed to cancel order');
    }
  };

  const handleUpdateOrderShipping = async (orderId, newShippingOption) => {
    setLoading(true);
    try {
      const payload = {
        userEmail: selectedUser.email,
        shippingOption: newShippingOption
      };
      await api.put(`/admins/users/${selectedUser.id}/orders/${orderId}`, payload);
      toast.success('Order shipping option updated successfully');
      const updatedOrders = selectedUser.orders.map(o =>
        o.orderId === orderId ? { ...o, shippingOption: newShippingOption } : o
      );
      setSelectedItem(prev => ({ ...prev, data: { ...prev.data, orders: updatedOrders } }));
    } catch (err) {
      toast.error('Failed to update shipping option');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeOrderService = async (orderId, itemIndex, newServiceId, previousServiceName) => {
    if (!newServiceId) return;
    const newService = services.find(s => s.id === newServiceId);
    if (!window.confirm(`Change this item from "${previousServiceName}" to "${newService?.name || newServiceId}"?`)) return;

    setLoading(true);
    try {
      const payload = { userEmail: selectedUser.email, itemIndex, newServiceId };
      const res = await api.put(`/admins/users/${selectedUser.id}/orders/${orderId}/change-service`, payload);
      toast.success('Service changed.');
      if (res.data.priceChanged) {
        toast.warn(`Note: price changed from £${res.data.previousPrice.toFixed(2)} to £${res.data.newPrice.toFixed(2)}. The customer's original payment was NOT automatically adjusted - refund or collect the difference manually if needed.`, { autoClose: 12000 });
      }
      const updatedOrders = selectedUser.orders.map(o => o.orderId === orderId ? res.data.order : o);
      setSelectedItem(prev => ({ ...prev, data: { ...prev.data, orders: updatedOrders } }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change service.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to reset this order back to the start? This will move it back to the document upload stage for the user and clear any generated letter drafts.')) return;
    try {
      const payload = {
        userEmail: selectedUser.email,
        status: 'awaiting_submission'
      };
      await api.put(`/admins/users/${selectedUser.id}/orders/${orderId}`, payload);
      toast.success('Order reset to start');
      const updatedOrders = selectedUser.orders.map(o =>
        o.orderId === orderId ? { ...o, status: 'awaiting_submission', submittedAt: null, completedAt: null, letterContent: null, aiTranscript: null, letterBlobName: null } : o
      );
      setSelectedItem(prev => ({ ...prev, data: { ...prev.data, orders: updatedOrders } }));
    } catch (err) {
      toast.error('Failed to reset order');
    }
  };

  const handlePushToDoctorEditing = async (orderId) => {
    if (!window.confirm('Are you sure you want to push this order back to the doctor editing stage? This will clear any final generated letter and allow the doctor to re-edit the draft.')) return;
    setLoading(true);
    try {
      const payload = {
        userEmail: selectedUser.email,
        status: 'ai_draft_generated'
      };
      await api.put(`/admins/users/${selectedUser.id}/orders/${orderId}`, payload);
      toast.success('Order pushed back to doctor editing.');
      // Update local state: clear completedAt and letterBlobName, but keep letterContent and aiTranscript
      const updatedOrders = selectedUser.orders.map(o =>
        o.orderId === orderId ? { ...o, status: 'ai_draft_generated', completedAt: null, letterBlobName: null } : o
      );
      setSelectedItem(prev => ({ ...prev, data: { ...prev.data, orders: updatedOrders } }));
    } catch (err) {
      toast.error('Failed to push order to doctor editing');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDoctor = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        doctorEmail: selectedItem.data.originalEmail || selectedItem.data.email,
        name: selectedDoctor.name,
        email: selectedDoctor.email,
        gmcNumber: selectedDoctor.gmcNumber
      };
      const res = await api.put(`/admins/doctors/${selectedDoctor.id}`, payload);
      toast.success('Doctor updated successfully');
      setSearchResults(prev => ({ ...prev, doctors: prev.doctors.map(d => d.id === selectedDoctor.id ? res.data.doctor : d) }));
      setSelectedItem({ type: 'doctors', data: res.data.doctor });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update doctor');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        adminEmail: selectedItem.data.originalEmail, // Use the untrimmed/original value for lookup
        name: selectedAdmin.name,
        email: selectedAdmin.email
      };
      const res = await api.put(`/admins/admins/${selectedAdmin.id}`, payload);
      toast.success('Admin updated successfully');
      const updatedAdmin = res.data.admin;
      setSearchResults(prev => ({ ...prev, admins: prev.admins.map(a => a.id === updatedAdmin.id ? updatedAdmin : a) }));
      setSelectedItem({ type: 'admins', data: updatedAdmin });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update admin');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestDoctorReset = async (doctor) => {
    if (!window.confirm(`Send password reset link to Dr. ${doctor.name} (${doctor.email})?`)) return;
    setLoading(true);
    try {
      await api.post(`/admins/doctors/${doctor.id}/request-reset-code`, { doctorEmail: doctor.email });
      toast.success(`Reset code sent to ${doctor.email}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAdminReset = async (admin) => {
    if (!window.confirm(`Send password reset link to Admin ${admin.name} (${admin.email})?`)) return;
    setLoading(true);
    try {
      await api.post(`/admins/request-reset-code`, { email: admin.email }); // Re-use existing admin reset endpoint
      toast.success(`Reset code sent to ${admin.email}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const closeDetailView = () => {
    setSelectedItem(null);
  };

  // Memoize search results to avoid re-rendering tables unnecessarily
  const memoizedUsers = useMemo(() => searchResults.users, [searchResults.users]);
  const memoizedDoctors = useMemo(() => searchResults.doctors, [searchResults.doctors]);
  const memoizedAdmins = useMemo(() => searchResults.admins, [searchResults.admins]);

  const isDetailViewOpen = !!selectedItem;

  return (
    <Container>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Admin Portal</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {adminName && <h3>Welcome, {adminName}</h3>}
          <Link to="/pricing"><Button as="span">Service Pricing</Button></Link>
          <Link to="/edit-services"><Button as="span">Edit Services</Button></Link>
          <Link to="/discount-codes"><Button as="span">Discount Codes</Button></Link>
          <Link to="/emails"><Button as="span">Customer Emails</Button></Link>
          <Button onClick={handleLogout} style={{ backgroundColor: '#6c757d' }}>Logout</Button>
        </div>
      </div>
      <SearchBar>
        <Input
          placeholder={`Search ${searchType}...`}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
        />
        <Select value={searchType} onChange={e => { setSearchType(e.target.value); setQuery(''); resetSearchState(); }}>
          <option value="users">Users</option>
          <option value="doctors">Doctors</option>
          <option value="admins">Admins</option>
        </Select>
        <Button onClick={handleSearchSubmit} disabled={loading}>{loading ? 'Searching...' : 'Search'}</Button>
      </SearchBar>

      {isDetailViewOpen && <CloseDetailButton onClick={closeDetailView}>&times;</CloseDetailButton>}

      {selectedUser && (
        <DetailView>
          <h2>Editing User: {selectedUser.name}</h2>
          <form onSubmit={handleUpdateUser}>
            <FormGroup>
              <label>Name</label>
              <Input value={selectedUser.name || ''} onChange={e => setSelectedItem({ ...selectedItem, data: { ...selectedUser, name: e.target.value } })} />
            </FormGroup>
            <FormGroup>
              <label>Email</label>
              <Input value={selectedUser.email || ''} onChange={e => setSelectedItem({ ...selectedItem, data: { ...selectedUser, email: e.target.value } })} />
            </FormGroup>
            <FormGroup>
              <label>DOB</label>
              <Input type="date" value={selectedUser.dob ? selectedUser.dob.split('T')[0] : ''} onChange={e => setSelectedItem({ ...selectedItem, data: { ...selectedUser, dob: e.target.value } })} />
            </FormGroup>
            <FormGroup>
              <label>Phone</label>
              <Input value={selectedUser.phone || ''} onChange={e => setSelectedItem({ ...selectedItem, data: { ...selectedUser, phone: e.target.value } })} />
            </FormGroup>
            <FormGroup>
              <label>Gender</label>
              <Select value={selectedUser.sex || selectedUser.gender || ''} onChange={e => setSelectedItem({ ...selectedItem, data: { ...selectedUser, sex: e.target.value } })}>
                <option value="">Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </Select>
            </FormGroup>

            <h3>Address</h3>
            <FormGroup>
              <label>Line 1</label>
              <Input value={selectedUser.shipping_address?.line1 || ''} onChange={e => handleAddressChange('line1', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <label>City</label>
              <Input value={selectedUser.shipping_address?.city || ''} onChange={e => handleAddressChange('city', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <label>Postcode</label>
              <Input value={selectedUser.shipping_address?.postal_code || ''} onChange={e => handleAddressChange('postal_code', e.target.value)} />
            </FormGroup>

            <Button type="submit" disabled={loading}>Save User Details</Button>
          </form>

          <h3>Orders</h3>
          {selectedUser.orders?.map(order => (
            <OrderCard key={order.orderId}>
              <p><strong>ID:</strong> {order.orderId}</p>
              <p><strong>Date:</strong> {new Date(order.date).toLocaleDateString()}</p>
              <p><strong>Status:</strong> {order.status}</p>
              <p><strong>Total:</strong> £{order.total}</p>
              {order.status === 'cancelled' && (
                <p style={{ color: '#d9534f', fontWeight: 'bold', margin: '10px 0' }}>Order Cancelled</p>
              )}
              {order.items?.map((item, itemIndex) => (
                <FormGroup key={itemIndex} style={{ marginBottom: '0.75rem' }}>
                  <label>Item {order.items.length > 1 ? itemIndex + 1 : ''}: {item.name} (x{item.quantity}, £{Number(item.price).toFixed(2)} each)</label>
                  <Select
                    value=""
                    onChange={(e) => handleChangeOrderService(order.orderId, itemIndex, e.target.value, item.name)}
                    disabled={loading || order.status === 'cancelled'}
                  >
                    <option value="">Change to a different service...</option>
                    {Object.entries(servicesByCategory).map(([category, categoryServices]) => (
                      <optgroup key={category} label={category}>
                        {categoryServices.map(s => (
                          <option key={s.id} value={s.id} disabled={s.id === item.id}>{s.name}</option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                </FormGroup>
              ))}
              <FormGroup>
                <label>Delivery Option</label>
                <Select
                  value={order.shippingOption || 'email'}
                  onChange={(e) => handleUpdateOrderShipping(order.orderId, e.target.value)}
                  disabled={loading}
                >
                  <option value="email">Email</option>
                  <option value="post">Post</option>
                </Select>
              </FormGroup>
              {order.status !== 'cancelled' ? (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button className="danger" onClick={() => handleCancelOrder(order.orderId)} disabled={loading}>Cancel Order</Button>
                  <Button onClick={() => handleResetOrder(order.orderId)} disabled={loading}>Reset to Start</Button>
                  {order.status === 'completed' && ( // Only show if it was completed, to push back to editing
                    <Button onClick={() => handlePushToDoctorEditing(order.orderId)} disabled={loading}>Push to Doctor Editing</Button>
                  )}
                </div>
              ) : (
                <Button onClick={() => handleResetOrder(order.id)} disabled={loading}>Reset to Start</Button>
              )}
            </OrderCard>
          ))}
        </DetailView>
      )}

      {selectedDoctor && (
        <DetailView>
          <h2>Editing Doctor: {selectedDoctor.name}</h2>
          <form onSubmit={handleUpdateDoctor}>
            <FormGroup>
              <label>Name</label>
              <Input value={selectedDoctor.name || ''} onChange={e => setSelectedItem({ ...selectedItem, data: { ...selectedDoctor, name: e.target.value } })} />
            </FormGroup>
            <FormGroup>
              <label>Email</label>
              <Input type="email" value={selectedDoctor.email || ''} onChange={e => setSelectedItem({ ...selectedItem, data: { ...selectedDoctor, email: e.target.value } })} />
            </FormGroup>
            <FormGroup>
              <label>GMC Number</label>
              <Input value={selectedDoctor.gmcNumber || ''} onChange={e => setSelectedItem({ ...selectedItem, data: { ...selectedDoctor, gmcNumber: e.target.value } })} />
            </FormGroup>
            <Button type="submit" disabled={loading}>Save Doctor Details</Button>
          </form>
          <Button onClick={() => handleRequestDoctorReset(selectedDoctor)} disabled={loading} style={{ marginTop: '1rem' }}>Send Reset Password Link</Button>
        </DetailView>
      )}

      {selectedAdmin && (
        <DetailView>
          <h2>Editing Admin: {selectedAdmin.name}</h2>
          <form onSubmit={handleUpdateAdmin}>
            <FormGroup>
              <label>Name</label>
              <Input value={selectedAdmin.name || ''} onChange={e => setSelectedItem({ ...selectedItem, data: { ...selectedAdmin, name: e.target.value } })} />
            </FormGroup>
            <FormGroup>
              <label>Email</label>
              <Input type="email" value={selectedAdmin.email || ''} onChange={e => setSelectedItem({ ...selectedItem, data: { ...selectedAdmin, email: e.target.value } })} />
            </FormGroup>
            <Button type="submit" disabled={loading}>Save Admin Details</Button>
          </form>
          <Button onClick={() => handleRequestAdminReset(selectedAdmin)} disabled={loading} style={{ marginTop: '1rem' }}>Send Reset Password Link</Button>
        </DetailView>
      )}

      {!isDetailViewOpen && (
        <ResultsTable>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>DOB</th>
              <th>Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {memoizedUsers.map(user => (
              <tr key={user.id} onClick={() => setSelectedItem({ type: 'users', data: { ...user, originalEmail: user.email } })}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{user.dob ? new Date(user.dob).toLocaleDateString('en-GB') : 'N/A'}</td>
                <td>User</td>
                <td></td> {/* Click row to edit */}
              </tr>
            ))}
            {memoizedDoctors.map(doctor => (
              <tr key={doctor.id}>
                <td>{doctor.name}</td>
                <td>{doctor.email}</td>
                <td>N/A</td>
                <td>Doctor</td>
                <td>
                  <Button onClick={() => setSelectedItem({ type: 'doctors', data: { ...doctor, originalEmail: doctor.email } })} style={{ marginRight: '0.5rem' }}>Edit</Button>
                  <Button onClick={() => handleRequestDoctorReset(doctor)} disabled={loading}>Reset Password</Button>
                </td>
              </tr>
            ))}
            {memoizedAdmins.map(admin => (
              <tr key={admin.id}>
                <td>{admin.name}</td>
                <td>{admin.email}</td>
                <td>N/A</td>
                <td>Admin</td>
                <td>
                  <Button onClick={() => setSelectedItem({ type: 'admins', data: { ...admin, originalEmail: admin.email } })} style={{ marginRight: '0.5rem' }}>Edit</Button>
                  <Button onClick={() => handleRequestAdminReset(admin)} disabled={loading}>Reset Password</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </ResultsTable>
      )}
    </Container>
  );
};

export default AdminDashboard;
